/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ProfileRouteArt / ProfileElevation
 * ----------------------------------
 * The athlete profile header's artwork: the sign-in page's route map and
 * elevation profile, recoloured for the dark header. Purely decorative — the
 * geometry is the fixed-seed ROUTE_ART from components/signin/RouteMapArt, not
 * the athlete's own activities, so it costs no data and no computation per user.
 *
 * Gradient ids come from useId so two headers on one page never share one.
 */

import React, { useId } from 'react';
import { ROUTE_ART } from '../../../components/signin/RouteMapArt';

const {
  MW, MH, ROADS, ROUTE_D, CHEVRONS, KM_MARKS, START, FINISH, DASH,
  EW, EH, ELEV, SPLITS, catmull,
} = ROUTE_ART;

// Road paths never change, so build them once.
const ROAD_PATHS = ROADS.map((r) => ({ d: catmull(r.p), arterial: !!r.arterial }));

export function ProfileRouteArt() {
  const gid = `apRoute${useId().replace(/:/g, '')}`;
  return (
    <div className="ap-art" aria-hidden="true">
      <svg viewBox={`0 0 ${MW} ${MH}`} preserveAspectRatio="xMaxYMid slice">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FF6A1A" />
            <stop offset="100%" stopColor="#FFB68F" />
          </linearGradient>
        </defs>
        {ROAD_PATHS.map((r, i) => <path key={`c${i}`} className={r.arterial ? 'ap-rm-artc' : 'ap-rm-roadc'} d={r.d} />)}
        {ROAD_PATHS.map((r, i) => <path key={`s${i}`} className={r.arterial ? 'ap-rm-art' : 'ap-rm-road'} d={r.d} />)}
        <path className="ap-rm-park" d="M118 148 q46-22 84-8 q34 13 30 44 q-4 32-44 40 q-44 9-70-14 q-24-22 0-62z" />
        <path className="ap-rm-water" d="M340 232 q28-14 52 2 q22 15 14 32 q-9 18-40 14 q-32-5-38-24 q-5-16 12-24z" />
        <path className="ap-rm-case" d={ROUTE_D} />
        <path className="ap-rm-line" d={ROUTE_D} style={{ stroke: `url(#${gid})`, strokeDasharray: DASH, '--ap-dash': DASH }} />
        {CHEVRONS.map((c, i) => (
          <path
            key={`v${i}`}
            className="ap-rm-chev"
            d="M-2.6 -3 L2.2 0 L-2.6 3"
            transform={`translate(${c.x.toFixed(1)} ${c.y.toFixed(1)}) rotate(${c.deg.toFixed(1)})`}
          />
        ))}
        {KM_MARKS.map((m) => (
          <g key={`k${m.k}`}>
            <circle className="ap-rm-km" cx={m.x.toFixed(1)} cy={m.y.toFixed(1)} r="9" />
            <text className="ap-rm-kmt" x={m.x.toFixed(1)} y={m.y.toFixed(1)}>{m.k}</text>
          </g>
        ))}
        <circle className="ap-rm-start" cx={START.x.toFixed(1)} cy={START.y.toFixed(1)} r="8.5" />
        <text className="ap-rm-kmt" x={START.x.toFixed(1)} y={START.y.toFixed(1)}>S</text>
        <circle className="ap-rm-fin" cx={FINISH.x.toFixed(1)} cy={FINISH.y.toFixed(1)} r="8.5" />
        <text className="ap-rm-kmt ap-rm-fint" x={FINISH.x.toFixed(1)} y={FINISH.y.toFixed(1)}>F</text>
      </svg>
    </div>
  );
}

export function ProfileElevation() {
  const gid = `apElev${useId().replace(/:/g, '')}`;
  return (
    <div className="ap-ridge" aria-hidden="true">
      <span className="ap-ridge-lab">Elevation</span>
      <svg viewBox={`0 0 ${EW} ${EH}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF6A1A" stopOpacity="0.38" />
            <stop offset="100%" stopColor="#FF6A1A" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {SPLITS.map((x) => <line key={x} className="ap-ridge-split" x1={x} y1="0" x2={x} y2={EH} />)}
        <path d={ELEV.area} style={{ fill: `url(#${gid})` }} />
        <path className="ap-ridge-line" d={ELEV.line} />
      </svg>
    </div>
  );
}
